import { useEffect, useRef, useMemo, useState } from 'react';
import { expandQuery, getSynonymPhrases } from '../lib/synonym-expander';
// Field weights for score merging
const FIELD_WEIGHTS = {
    title: 100,
    sku: 90,
    tags: 50,
    categories: 30,
    usage: 20,
    search: 10,
};
// Products matched only through synonym expansion score at this fraction of normal
const SYNONYM_SCORE_FACTOR = 0.4;
// Default cap on the number of results returned, used when no maxResults is configured
const DEFAULT_MAX_RESULTS = 30;
// Default multiplier applied to a product's sales-popularity bonus, used when no
// salesBoost is configured
const DEFAULT_SALES_BOOST = 30;
/**
 * Normalize an array field into a space-separated string for FlexSearch.
 * Handles plain strings, string arrays, and object arrays (e.g. CategoryItem[]).
 */
function flattenField(product, field) {
    const value = product[field];
    if (Array.isArray(value)) {
        return value.map((item) => {
            if (typeof item === 'string')
                return item;
            if (item && typeof item === 'object')
                return [item.name, item.slug].filter(Boolean).join(' ');
            return '';
        }).filter(Boolean).join(' ');
    }
    if (typeof value === 'string')
        return value;
    return '';
}
/**
 * Search engine hook that manages FlexSearch index with fallback.
 */
export function useSearchEngine(products, synonyms, enabledFields, maxResults, salesBoost) {
    const indexRef = useRef(null);
    const [isReady, setIsReady] = useState(false);
    const [useFallback, setUseFallback] = useState(false);
    // Initialize FlexSearch index when products change
    useEffect(() => {
        if (!products.length)
            return;
        let cancelled = false;
        import('flexsearch').then((FlexSearchModule) => {
            if (cancelled)
                return;
            // FlexSearch's ESM build only has a default export (`export default {Index, Document, ...}`),
            // so a plain named destructure yields `undefined` and `new Document()` throws "not a constructor".
            const FlexSearch = FlexSearchModule.default ?? FlexSearchModule;
            const { Document } = FlexSearch;
            const index = new Document({
                document: {
                    id: 'id',
                    index: [
                        { field: 'title', tokenize: 'forward', resolution: 9 },
                        { field: 'sku', tokenize: 'full', resolution: 9 },
                        { field: 'tags', tokenize: 'forward', resolution: 5 },
                        { field: 'categories', tokenize: 'forward', resolution: 5 },
                        { field: 'search', tokenize: 'forward', resolution: 3 },
                        { field: 'usage', tokenize: 'strict', resolution: 5 },
                    ],
                    store: ['title', 'sku', 'url', 'image', 'price', 'categories', 'tags', 'usage'],
                },
                cache: true,
            });
            // Add products — flatten all fields to strings for FlexSearch
            for (const product of products) {
                const doc = { id: product.id };
                for (const field of Object.keys(FIELD_WEIGHTS)) {
                    doc[field] = flattenField(product, field);
                }
                // Add non-indexed display fields
                doc.url = product.url;
                doc.image = product.image;
                doc.price = product.price;
                try {
                    index.add(doc);
                }
                catch (e) {
                    console.warn('EnoWooSearch: Failed to add product', product.id, e);
                }
            }
            indexRef.current = index;
            setIsReady(true);
            setUseFallback(false);
        }).catch((error) => {
            if (cancelled)
                return;
            console.warn('EnoWooSearch: FlexSearch unavailable, using fallback search', error);
            setUseFallback(true);
            setIsReady(true);
        });
        return () => {
            cancelled = true;
            indexRef.current = null;
            setIsReady(false);
        };
    }, [products]);
    const activeWeights = useMemo(() => {
        if (!enabledFields || enabledFields.length === 0)
            return FIELD_WEIGHTS;
        return Object.fromEntries(Object.entries(FIELD_WEIGHTS).filter(([field]) => enabledFields.includes(field)));
    }, [enabledFields]);
    const resultLimit = maxResults && maxResults > 0 ? maxResults : DEFAULT_MAX_RESULTS;
    const salesWeight = salesBoost !== undefined && salesBoost >= 0 ? salesBoost : DEFAULT_SALES_BOOST;
    const search = useMemo(() => {
        return (query) => {
            if (!query.trim() || !isReady)
                return [];
            const expandedQuery = expandQuery(query, synonyms);
            if (useFallback) {
                return fallbackSearch(products, expandedQuery, query, activeWeights, synonyms, resultLimit, salesWeight);
            }
            return flexSearch(indexRef.current, products, expandedQuery, query, activeWeights, synonyms, resultLimit, salesWeight);
        };
    }, [products, synonyms, isReady, useFallback, activeWeights, resultLimit, salesWeight]);
    return { search, isReady };
}
/**
 * FlexSearch-based search with weighted field scoring.
 */
function flexSearch(index, products, query, originalQuery, fieldWeights, synonyms, resultLimit, salesWeight) {
    if (!index)
        return [];
    const queryLower = query.toLowerCase();
    const originalLower = originalQuery.toLowerCase();
    const synonymPhrases = getSynonymPhrases(originalLower, synonyms);
    const productById = new Map(products.map(p => [p.id, p]));
    const fieldResults = new Map();
    const primaryIds = new Set();
    // Tier 1: search with original (pre-expansion) query — full field scores
    for (const field of Object.keys(fieldWeights)) {
        try {
            const results = index.search(originalLower, { limit: 200, field });
            if (Array.isArray(results)) {
                for (const entry of results) {
                    if (entry && Array.isArray(entry.result)) {
                        const weight = fieldWeights[field] || 1;
                        for (const id of entry.result) {
                            const productId = typeof id === 'number' ? id : parseInt(String(id), 10);
                            if (isNaN(productId))
                                continue;
                            primaryIds.add(productId);
                            const existing = fieldResults.get(productId);
                            if (existing) {
                                existing.score += weight;
                                existing.fields.add(field);
                            }
                            else {
                                fieldResults.set(productId, { score: weight, fields: new Set([field]) });
                            }
                        }
                    }
                }
            }
        }
        catch (e) { }
    }
    // Tier 2: search with expanded (synonym) query — reduced scores for NEW products only
    if (queryLower !== originalLower) {
        for (const field of Object.keys(fieldWeights)) {
            try {
                const results = index.search(queryLower, { limit: 200, field });
                if (Array.isArray(results)) {
                    for (const entry of results) {
                        if (entry && Array.isArray(entry.result)) {
                            const weight = (fieldWeights[field] || 1) * SYNONYM_SCORE_FACTOR;
                            for (const id of entry.result) {
                                const productId = typeof id === 'number' ? id : parseInt(String(id), 10);
                                if (isNaN(productId) || primaryIds.has(productId))
                                    continue;
                                // FlexSearch matches the expanded query token-by-token (OR), which would
                                // let e.g. "xlm" → "XLR male" loosely match an "XLR female" product via
                                // the shared word "XLR". Require the full original query or a complete
                                // synonym phrase to actually be present before accepting the match.
                                const product = productById.get(productId);
                                if (!product || !matchesQueryOrSynonymPhrase(product, fieldWeights, originalLower, synonymPhrases))
                                    continue;
                                const existing = fieldResults.get(productId);
                                if (existing) {
                                    existing.score += weight;
                                    existing.fields.add(field);
                                }
                                else {
                                    fieldResults.set(productId, { score: weight, fields: new Set([field]) });
                                }
                            }
                        }
                    }
                }
            }
            catch (e) { }
        }
    }
    // Fallback: direct substring check for title and SKU (only if those fields are enabled)
    const originalTokens = originalLower.split(/\s+/).filter(Boolean);
    const titleWeight = fieldWeights.title ?? 0;
    const skuWeight = fieldWeights.sku ?? 0;
    for (const product of products) {
        if (fieldResults.has(product.id))
            continue;
        const titleLower = titleWeight > 0 ? (product.title || '').toLowerCase() : '';
        const skuLower = skuWeight > 0 ? (product.sku || '').toLowerCase() : '';
        if (titleWeight > 0 && originalTokens.some(t => literalTokenMatches(titleLower, t))) {
            fieldResults.set(product.id, { score: titleWeight, fields: new Set(['title']) });
        }
        else if (skuWeight > 0 && originalTokens.some(t => literalTokenMatches(skuLower, t))) {
            fieldResults.set(product.id, { score: skuWeight, fields: new Set(['sku']) });
        }
        else if (titleWeight > 0 && synonymPhrases.some(p => textContains(titleLower, p))) {
            fieldResults.set(product.id, { score: titleWeight * SYNONYM_SCORE_FACTOR, fields: new Set(['title']) });
        }
        else if (skuWeight > 0 && synonymPhrases.some(p => textContains(skuLower, p))) {
            fieldResults.set(product.id, { score: skuWeight * SYNONYM_SCORE_FACTOR, fields: new Set(['sku']) });
        }
    }
    // FlexSearch's own token/prefix matching (Tier 1 above) can return near the entire
    // catalog for a very short single-word query like "l" or "90" (e.g. forward
    // tokenization indexes "l" as a prefix of "Lautsprecherkabel"). Re-verify such
    // candidates require a genuine whole-word match before keeping them.
    if (originalTokens.length === 1 && originalLower.length <= 2) {
        for (const productId of Array.from(fieldResults.keys())) {
            const product = productById.get(productId);
            if (!product || !matchesQueryOrSynonymPhrase(product, fieldWeights, originalLower, synonymPhrases)) {
                fieldResults.delete(productId);
            }
        }
    }
    filterRequiringAllTokens(fieldResults, products, originalTokens, fieldWeights, synonyms);
    return buildResults(products, fieldResults, queryLower, originalQuery, resultLimit, salesWeight);
}
/**
 * Fallback simple string matching (used if FlexSearch fails to load).
 */
function fallbackSearch(products, query, originalQuery, fieldWeights, synonyms, resultLimit, salesWeight) {
    const originalLower = originalQuery.toLowerCase();
    const queryLower = query.toLowerCase();
    const originalTokens = originalLower.split(/\s+/).filter(Boolean);
    const synonymPhrases = getSynonymPhrases(originalLower, synonyms);
    if (!originalTokens.length)
        return [];
    const productById = new Map(products.map(p => [p.id, p]));
    const fieldResults = new Map();
    for (const product of products) {
        let totalScore = 0;
        const matched = new Set();
        let isPrimary = false;
        // Tier 1: check original query tokens at full weight
        for (const field of Object.keys(fieldWeights)) {
            const texts = getTexts(product, field);
            const weight = fieldWeights[field];
            for (const text of texts) {
                const lower = (text || '').toLowerCase();
                if (originalTokens.some(t => literalTokenMatches(lower, t))) {
                    totalScore += weight;
                    matched.add(field);
                    isPrimary = true;
                    break;
                }
            }
        }
        // Tier 2: check for a complete synonym phrase at reduced weight (only if no primary
        // match). Matching on individual expanded words would let e.g. "xlm" → "XLR male"
        // loosely match an "XLR female" product via the shared word "XLR".
        if (!isPrimary && synonymPhrases.length > 0) {
            for (const field of Object.keys(fieldWeights)) {
                const texts = getTexts(product, field);
                const weight = fieldWeights[field] * SYNONYM_SCORE_FACTOR;
                for (const text of texts) {
                    const lower = (text || '').toLowerCase();
                    if (synonymPhrases.some(p => textContains(lower, p))) {
                        totalScore += weight;
                        matched.add(field);
                        break;
                    }
                }
            }
        }
        if (totalScore > 0) {
            fieldResults.set(product.id, { score: totalScore, fields: matched });
        }
    }
    // Very short single-word queries (e.g. a bare "L" or "90") still slip through the
    // per-field substring checks above via short words inside unrelated text. Re-verify
    // such candidates require a genuine whole-word match in the active fields.
    if (originalTokens.length === 1 && originalLower.length <= 2) {
        for (const productId of Array.from(fieldResults.keys())) {
            const product = productById.get(productId);
            if (!product || !matchesQueryOrSynonymPhrase(product, fieldWeights, originalLower, synonymPhrases)) {
                fieldResults.delete(productId);
            }
        }
    }
    filterRequiringAllTokens(fieldResults, products, originalTokens, fieldWeights, synonyms);
    return buildResults(products, fieldResults, queryLower, originalQuery, resultLimit, salesWeight);
}
/**
 * Check whether `text` contains `term` as a meaningful match. Plain substring checks
 * break down for very short terms (1-2 characters): a bare "l" or "90" — entered as a
 * synonym dictionary key/value to mean "Left/Angled" or a 90° connector — appears
 * inside countless unrelated words ("VIABLUE", "Kabelschuhe", "Lautsprecherkabel",
 * "1900"...), so a substring search returns nearly the entire catalog. Short terms are
 * therefore required to appear as a whole word (word-boundary match); longer terms
 * keep the looser substring check so partial-word matches keep working as before.
 */
function textContains(text, term) {
    if (!term)
        return false;
    if (term.length <= 2) {
        const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return new RegExp(`\\b${escaped}\\b`).test(text);
    }
    return text.includes(term);
}
/**
 * Check whether `text` contains `term` as a standalone, whitespace-delimited word —
 * stricter than a generic word-boundary match. A query like "l" would, under a plain
 * `\bl\b` check, also match the "L" inside the ubiquitous "(L,R)" stereo-channel
 * marker found in countless cable titles (parentheses/commas are non-word characters,
 * so they count as boundaries too) — making the short-query check just as noisy as a
 * substring search. Requiring whitespace (or string start/end) on both sides excludes
 * those punctuation-delimited notations while still matching genuine standalone words
 * such as "L Angle".
 */
function isStandaloneWord(text, term) {
    if (!term)
        return false;
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(?:^|\\s)${escaped}(?:\\s|$)`).test(text);
}
/**
 * Check whether some word in `text` begins with `prefix`. Used to let a short
 * (2-character) token in a multi-word query match a word that's only partially
 * typed (e.g. "pa" matching "Pair") — without this, a query like "rca pa" finds
 * nothing while "rca pai" suddenly finds matches, which feels broken to a user
 * typing incrementally. Limited to 2-char tokens in filterRequiringAllTokens,
 * where the other query words already keep the candidate set narrow.
 */
function wordStartsWith(text, prefix) {
    if (!prefix)
        return false;
    const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(?:^|\\s)${escaped}`).test(text);
}
/**
 * Check whether `text` contains the literal query token `token`. Short tokens (1-2
 * characters) use the stricter whitespace-delimited check (see isStandaloneWord);
 * longer tokens use a plain substring check, as before.
 */
function literalTokenMatches(text, token) {
    if (!token)
        return false;
    return token.length <= 2 ? isStandaloneWord(text, token) : text.includes(token);
}
/**
 * Check whether a product's active/searchable fields contain the literal query,
 * or one of its complete synonym phrases, as a meaningful match. Used to verify that
 * a synonym-based candidate is a genuine match rather than a loose token overlap
 * (e.g. matching "XLR female" for a search of "xlm" just because both phrases
 * happen to contain the word "XLR").
 */
function matchesQueryOrSynonymPhrase(product, fieldWeights, originalLower, synonymPhrases) {
    const activeFields = Object.keys(fieldWeights).filter(field => fieldWeights[field] > 0);
    const combinedText = activeFields.map(field => flattenField(product, field)).join(' ').toLowerCase();
    if (literalTokenMatches(combinedText, originalLower))
        return true;
    return synonymPhrases.some(phrase => textContains(combinedText, phrase));
}
/**
 * Remove candidates that don't satisfy every word of a multi-word query (directly, or
 * via a complete synonym phrase of that specific word) from the active/searchable
 * fields. Without this, a query like "rca pair" would match products containing only
 * "rca" or only "pair" because the underlying field search matches on any token (OR),
 * not all of them (AND).
 *
 * IMPORTANT: this must run on the full candidate map BEFORE buildResults ranks and
 * slices it down to the configured result limit — otherwise a genuine match that simply
 * didn't score into that pre-filter cut is discarded before this AND-check ever sees it.
 * This is exactly what hid "... XLR male 90º" cables from a search for "xlr l": dozens of
 * other "xlr"-only matches outscored them into the top cut, and filtering afterwards
 * could only narrow that already-wrong set further, never recover the missing genuine match.
 *
 * Alternatives are kept as whole phrases (via getSynonymPhrases) rather than split
 * into individual words — splitting "xlr male" into "xlr"/"male" would let the
 * generic word "xlr" alone satisfy the check, causing e.g. "xlm xlf" to match any
 * product that merely mentions "XLR" somewhere, regardless of male/female.
 *
 * Mutates `fieldResults` in place, removing any candidate that fails the AND-check.
 */
function filterRequiringAllTokens(fieldResults, products, originalTokens, fieldWeights, synonyms) {
    if (originalTokens.length < 2)
        return;
    const productById = new Map(products.map(p => [p.id, p]));
    const activeFields = Object.keys(fieldWeights).filter(field => fieldWeights[field] > 0);
    const tokenChecks = originalTokens.map(token => {
        const phrases = getSynonymPhrases(token, synonyms);
        return (text) => literalTokenMatches(text, token) ||
            (token.length === 2 && wordStartsWith(text, token)) ||
            phrases.some(phrase => textContains(text, phrase));
    });
    for (const id of Array.from(fieldResults.keys())) {
        const product = productById.get(id);
        if (!product) {
            fieldResults.delete(id);
            continue;
        }
        const combinedText = activeFields.map(field => flattenField(product, field)).join(' ').toLowerCase();
        if (!tokenChecks.every(check => check(combinedText))) {
            fieldResults.delete(id);
        }
    }
}
function getTexts(product, field) {
    const v = product[field];
    if (Array.isArray(v)) {
        return v.map((item) => {
            if (typeof item === 'string')
                return item;
            if (item && typeof item === 'object')
                return [item.name, item.slug].filter(Boolean).join(' ');
            return '';
        }).filter((s) => s.length > 0);
    }
    if (typeof v === 'string')
        return [v];
    return [];
}
function buildResults(products, fieldResults, queryLower, originalQuery, resultLimit, salesWeight) {
    const originalTokens = originalQuery.toLowerCase().split(/\s+/).filter(Boolean);
    return Array.from(fieldResults.entries())
        .map(([id, { score, fields }]) => {
        const product = products.find(p => p.id === id);
        if (!product)
            return null;
        let finalScore = score;
        const titleLower = (product.title || '').toLowerCase();
        // Bonus: title exact match
        if (titleLower === queryLower)
            finalScore += 200;
        // Bonus: title starts with first token
        const firstToken = queryLower.split(/\s+/)[0];
        if (firstToken && titleLower.startsWith(firstToken))
            finalScore += 50;
        // Bonus: each original query token found as a complete word in the title
        // This ensures products whose titles directly contain the search terms rank above
        // products that only match through field-score accumulation or sales boost.
        for (const token of originalTokens) {
            const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            if (new RegExp(`\\b${escaped}\\b`).test(titleLower)) {
                finalScore += 80;
            }
        }
        // Bonus: sales popularity (logarithmic scale so the boost has diminishing returns;
        // salesWeight is admin-configurable to control how strongly sales rank affects order)
        if (product.totalSales > 0 && salesWeight > 0)
            finalScore += Math.log(1 + product.totalSales) * salesWeight;
        return { product, score: finalScore, matchedFields: Array.from(fields) };
    })
        .filter((r) => r !== null)
        .sort((a, b) => b.score - a.score)
        .slice(0, resultLimit);
}
